import React from "react";
import { MATERIALS } from "../../constants";

const TOOLS = [
  { id: "wall",      icon: "▬",  tip: "Wall / Line (W)",        color: "#3b82f6" },
  { id: "select",    icon: "↖",  tip: "Select (S)",             color: "#6b7e94" },
  { id: "circle",    icon: "◯",  tip: "Circle (C)",             color: "#22d3ee" },
  { id: "arc",       icon: "◜",  tip: "Arc (3 points)",         color: "#22d3ee" },
  { id: "square",    icon: "□",  tip: "Square Line (S)",        color: "#22d3ee" },
  { id: "rect",      icon: "▭",  tip: "Rectangle Line (R)",     color: "#22d3ee" },
  { id: "squarewall",icon: "■",  tip: "Square Wall (Shift+S)",  color: "#fbbf24" },
  { id: "rectwall",  icon: "▲",  tip: "Rectangle Wall (Shift+R)",color: "#fbbf24" },
  { id: "spline",    icon: "〜", tip: "Spline (N points)",      color: "#a78bfa" },
  { id: "polyline",  icon: "⌐",  tip: "Polyline",              color: "#a78bfa" },
  { id: "room",      icon: "🏷", tip: "Room Label",            color: "#4ade80" },
  { id: "opening",   icon: "🚪", tip: "Opening (O)",           color: "#e8a838" },
  { id: "annotate",  icon: "✏",  tip: "Annotate (A)",          color: "#94a3b8" },
  { id: "dim",       icon: "↔",  tip: "Dimension (X)",         color: "#818cf8" },
  { id: "offset",    icon: "⬌",  tip: "Offset (F)",            color: "#22d3ee" },
  { id: "mirror",    icon: "⇋",  tip: "Mirror (I)",            color: "#f472b6" },
  { id: "trim",      icon: "✂",  tip: "Trim (K)",              color: "#fb923c" },
  { id: "extend",    icon: "↦",  tip: "Extend (EX)",           color: "#fb923c" },
  { id: "break",     icon: "⌇",  tip: "Break edge",            color: "#fb923c" },
];

export default function CADToolbar({
  mode, matId, setMatId, wallH, setWallH,
  offsetDist, setOffsetDist,
  showTransformPanel, onToggleTransform,
  onSetMode, onTemplates, onDrawing, onHeal, onToggleLayers,
  showLayerPanel, hasErr, errCount, selCount,
  show3D, onToggle3D,
  drawingMode, onSetDrawingMode,
  showAI, onToggleAI,
}) {
  const btn = (active, color) => ({
    width: 30, height: 26, borderRadius: 3, fontSize: 13,
    background: active ? color + "22" : "transparent",
    border:     active ? `1px solid ${color}60` : "1px solid transparent",
    color:      active ? color : "#2a3a4a",
    cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all .1s",
  });

  return (
    <div style={{
      position: "absolute", left: 0, top: 0, right: 0, height: 38,
      background: "#030508", borderBottom: "1px solid #0d1520",
      display: "flex", alignItems: "center", padding: "0 8px", gap: 2,
      zIndex: 10,
    }}>
      {TOOLS.map(t => (
        <button key={t.id} title={t.tip} onClick={() => onSetMode(t.id)}
          style={btn(mode === t.id, t.color)}>
          {t.icon}
        </button>
      ))}

      <div style={{ width: 1, background: "#0d1520", height: 20, margin: "0 4px" }}/>

      <select value={matId} onChange={e => setMatId(e.target.value)}
        style={{ background: "#04080f", border: "1px solid #0d1520", color: "#6b7e94",
          borderRadius: 3, padding: "2px 4px", fontSize: 9, fontFamily: "inherit", height: 24 }}>
        {Object.values(MATERIALS).map(m => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>

      <select value={wallH} onChange={e => setWallH(Number(e.target.value))}
        style={{ background: "#04080f", border: "1px solid #0d1520", color: "#6b7e94",
          borderRadius: 3, padding: "2px 4px", fontSize: 9, fontFamily: "inherit", height: 24 }}>
        {[2400, 2700, 3000, 3300, 3600, 4200].map(h => (
          <option key={h} value={h}>{h / 1000}m</option>
        ))}
      </select>

      {mode === "offset" && (
        <>
          <div style={{ width:1, background:"#0d1520", height:20, margin:"0 4px" }}/>
          <span style={{ fontSize:8, color:"#22d3ee", letterSpacing:1 }}>OFF</span>
          <input type="number" value={offsetDist}
            onChange={e => setOffsetDist(Math.max(1, Number(e.target.value)))}
            style={{ width:54, background:"#04080f", border:"1px solid #22d3ee40",
              color:"#22d3ee", borderRadius:3, padding:"2px 4px",
              fontSize:9, fontFamily:"inherit", height:24, textAlign:"right" }}
            title="Offset distance mm" min={1} max={50000} step={100}
          />
          <span style={{ fontSize:8, color:"#1e2a36" }}>mm</span>
        </>
      )}

      {mode === "mirror" && (
        <>
          <div style={{ width:1, background:"#0d1520", height:20, margin:"0 4px" }}/>
          <span style={{ fontSize:8, color:"#f472b6", letterSpacing:1 }}>
            MIRROR — click 2 pts for axis
          </span>
        </>
      )}

      <div style={{ width: 1, background: "#0d1520", height: 20, margin: "0 4px" }}/>

      {/* Drawing mode selector */}
      <select value={drawingMode || "architectural"}
        onChange={e => onSetDrawingMode?.(e.target.value)}
        title="Drawing mode — controls snap precision"
        style={{ background:"#04080f", border:"1px solid #0d1520", color:"#60a5fa",
          borderRadius:3, padding:"2px 6px", fontSize:9, fontFamily:"inherit", height:24,
          cursor:"pointer" }}>
        <option value="architectural">🏠 Architectural</option>
        <option value="mechanical">⚙ Mechanical</option>
        <option value="furniture">🪑 Furniture</option>
        <option value="electrical">⚡ Electrical</option>
        <option value="civil">🌍 Civil</option>
        <option value="schematic">📐 Schematic</option>
      </select>

      <div style={{ width: 1, background: "#0d1520", height: 20, margin: "0 4px" }}/>

      <button title="Templates (T)" onClick={onTemplates}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background:"transparent", border:"1px solid transparent", color:"#2a3a4a",
          cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}
        onMouseOver={e => e.currentTarget.style.borderColor="#1e2a36"}
        onMouseOut={e  => e.currentTarget.style.borderColor="transparent"}>
        📐 Templates
      </button>

      <button title="Construction Drawings (D)" onClick={onDrawing}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background:"transparent", border:"1px solid transparent", color:"#2a3a4a",
          cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}
        onMouseOver={e => e.currentTarget.style.borderColor="#1e2a36"}
        onMouseOut={e  => e.currentTarget.style.borderColor="transparent"}>
        🖨 Drawing
      </button>

      <button title="3D Preview" onClick={onToggle3D}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background: show3D ? "#3b82f615" : "transparent",
          border:     show3D ? "1px solid #3b82f640" : "1px solid transparent",
          color:      show3D ? "#60a5fa"  : "#2a3a4a",
          cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}
        onMouseOver={e => { if (!show3D) e.currentTarget.style.borderColor="#1e2a36"; }}
        onMouseOut={e  => { if (!show3D) e.currentTarget.style.borderColor="transparent"; }}>
        ◈ 3D
      </button>

      <button title="AI Assistant (Ctrl+J)" onClick={onToggleAI}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background: showAI ? "#818cf815" : "transparent",
          border:     showAI ? "1px solid #818cf840" : "1px solid transparent",
          color:      showAI ? "#818cf8"  : "#2a3a4a",
          cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}
        onMouseOver={e => { if (!showAI) e.currentTarget.style.borderColor="#1e2a36"; }}
        onMouseOut={e  => { if (!showAI) e.currentTarget.style.borderColor="transparent"; }}>
        ✦ AI
      </button>

      {selCount > 0 && (
        <button title="Transform Panel (P)" onClick={onToggleTransform}
          style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
            background: showTransformPanel ? "#f472b615" : "transparent",
            border:     showTransformPanel ? "1px solid #f472b630" : "1px solid #0d1520",
            color:      showTransformPanel ? "#f472b6" : "#3b4a5a",
            cursor:"pointer", fontFamily:"inherit" }}>
          ⟳ Transform
        </button>
      )}

      <div style={{ flex: 1 }}/>

      <button title="Heal (H)" onClick={onHeal}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background: hasErr ? "#1a0c00" : "transparent",
          border:     hasErr ? "1px solid #fb923c60" : "1px solid #0d1520",
          color:      hasErr ? "#fb923c" : "#2a3a4a",
          cursor:"pointer", fontFamily:"inherit" }}>
        {hasErr ? `⚕ ${errCount}` : "⚕"}
      </button>

      <button title="Layers (L)" onClick={onToggleLayers}
        style={{ padding:"2px 8px", height:24, borderRadius:3, fontSize:10,
          background: showLayerPanel ? "#facc1515" : "transparent",
          border:     showLayerPanel ? "1px solid #facc1530" : "1px solid transparent",
          color:      showLayerPanel ? "#facc15" : "#2a3a4a",
          cursor:"pointer", fontFamily:"inherit" }}>
        ◫ Layers
      </button>
    </div>
  );
}
